import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "./supabaseClient";
import {
  TrendingUp, TrendingDown, Bell, BellOff, DollarSign, Percent,
  Star, CheckCircle2, Trash2, Loader2, Plus, History, Mail,
  Database, RefreshCw,
} from "lucide-react";

const PROXY = "https://daytrade-proxy.onrender.com";
const ICON_STROKE = 2.25;

const EMAILJS_SERVICE_ID = "service_ihson4a";
const EMAILJS_TEMPLATE_ID = "template_y30vyxv";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_KEY || "";

const TODOS_ATIVOS = [
  "PETR4","VALE3","ITUB4","BBDC4","MGLU3","WEGE3","ABEV3","B3SA3",
  "RENT3","SUZB3","GGBR4","EMBR3","RADL3","EQTL3","SBSP3","VIVT3",
  "HGLG11","KNRI11","MXRF11","XPML11","BCFF11","VISC11","IRDM11",
  "IVVB11","BOVA11","HASH11","SMAL11","DIVO11",
  "BTC-USD","ETH-USD","BNB-USD","SOL-USD",
];

function paleta(tema) {
  if (tema === "claro") {
    return { card: "#FFFFFF", cardInner: "#F4F7FA", border: "#E2E8F0", textPrimary: "#172033", textSecondary: "#64748B", textFaint: "#94A3B8" };
  }
  return { card: "#0d1320", cardInner: "#111a27", border: "#1e2d45", textPrimary: "#fff", textSecondary: "#aaa", textFaint: "#444" };
}

// ── API calls ao proxy (substitui localStorage) ──────────────
async function carregarAlertasBanco() {
  try {
    const r = await authFetch(`${PROXY}/api/alertas`);
    const data = await r.json();
    if (data.success) {
      return data.data.map(a => ({
        id: a.id,
        ativo: a.ativo,
        tipo: a.tipo,
        direcao: a.direcao,
        valor: a.valor,
        email: a.emailAtivo,
        ativoFlag: a.ativoFlag,
        disparado: a.disparado,
        precoDisparo: a.precoDisparo,
        origemWatchlist: a.origemWatchlist,
        criadoEm: new Date(a.criadoEm).toLocaleString("pt-BR"),
        disparadoEm: a.disparadoEm ? new Date(a.disparadoEm).toLocaleString("pt-BR") : null,
      }));
    }
    return [];
  } catch { return []; }
}

async function criarAlertaBanco(alerta) {
  try {
    const r = await authFetch(`${PROXY}/api/alertas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ativo: alerta.ativo,
        tipo: alerta.tipo,
        direcao: alerta.direcao,
        valor: alerta.valor,
        emailAtivo: alerta.email,
      }),
    });
    const data = await r.json();
    return data.success ? data.id : null;
  } catch { return null; }
}

async function deletarAlertaBanco(id) {
  try {
    await authFetch(`${PROXY}/api/alertas/${id}`, { method: "DELETE" });
  } catch (e) { console.error("Erro ao deletar alerta:", e.message); }
}

async function atualizarAlertaBanco(id, campos) {
  try {
    await authFetch(`${PROXY}/api/alertas/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(campos),
    });
  } catch (e) { console.error("Erro ao atualizar alerta:", e.message); }
}

async function sendEmail(params) {
  if (!EMAILJS_PUBLIC_KEY) return false;
  try {
    const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: { ...params, horario: new Date().toLocaleString("pt-BR") },
      }),
    });
    return res.ok;
  } catch { return false; }
}

function fmt(v) { return v !== undefined && v !== null ? `R$ ${Number(v).toFixed(2)}` : "—"; }

// ── Notificação de alerta disparado ──────────────────────────
function AlertaNotificacao({ alerta, onDismiss, cores }) {
  useEffect(() => { const t = setTimeout(onDismiss, 10000); return () => clearTimeout(t); }, [onDismiss]);
  const cor = alerta.tipo === "subiu" ? "#00e5a0" : alerta.tipo === "caiu" ? "#ff4d6d" : "#ffd60a";
  const Icone = alerta.tipo === "subiu" ? TrendingUp : alerta.tipo === "caiu" ? TrendingDown : Bell;
  return (
    <div style={{ position: "fixed", top: "70px", right: "12px", left: "12px", zIndex: 9999, background: cores.card, border: `2px solid ${cor}`, borderRadius: "14px", padding: "16px", boxShadow: `0 8px 32px ${cor}44`, maxWidth: "400px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ color: cor, fontWeight: "700", fontSize: "14px", marginBottom: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
            <Icone size={15} strokeWidth={ICON_STROKE} /> ALERTA DISPARADO!
          </div>
          <div style={{ color: cores.textPrimary, fontSize: "16px", fontWeight: "700", fontFamily: "monospace" }}>{alerta.ativo}</div>
          <div style={{ color: cores.textSecondary, fontSize: "13px", marginTop: "4px" }}>{alerta.mensagem}</div>
          <div style={{ color: cor, fontSize: "18px", fontWeight: "700", fontFamily: "monospace", marginTop: "6px" }}>{fmt(alerta.precoAtual)}</div>
        </div>
        <button onClick={onDismiss} style={{ background: "none", border: "none", color: cores.textFaint, cursor: "pointer", fontSize: "20px" }}>×</button>
      </div>
    </div>
  );
}

// ── Card de alerta ────────────────────────────────────────────
function AlertaCard({ alerta, precoAtual, onDelete, onToggle, cores }) {
  const preco = precoAtual || 0;
  const diff = alerta.tipo === "preco_exato"
    ? ((preco - alerta.valor) / alerta.valor * 100).toFixed(2)
    : null;
  const cor = alerta.disparado ? cores.textFaint : alerta.ativoFlag ? "#00e5a0" : "#ffd60a";
  const progressoPct = alerta.tipo === "preco_exato" && preco && alerta.valor
    ? Math.min(100, Math.abs((preco / alerta.valor) * 100))
    : null;
  const IconeTipo = alerta.tipo === "preco_exato" ? DollarSign : alerta.direcao === "sobe" ? TrendingUp : TrendingDown;

  return (
    <div style={{ background: cores.card, border: `1px solid ${alerta.disparado ? cores.border : cor + "44"}`, borderRadius: "12px", padding: "14px", marginBottom: "10px", opacity: alerta.disparado ? 0.6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
            <span style={{ color: cores.textPrimary, fontWeight: "700", fontSize: "15px", fontFamily: "monospace" }}>{alerta.ativo}</span>
            <span style={{ background: `${cor}22`, color: cor, border: `1px solid ${cor}44`, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontFamily: "monospace", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <IconeTipo size={10} strokeWidth={ICON_STROKE} /> {alerta.tipo === "preco_exato" ? "PREÇO EXATO" : alerta.direcao === "sobe" ? "SUBIDA %" : "QUEDA %"}
            </span>
            {alerta.origemWatchlist && (
              <span style={{ background: "#ffd60a22", color: "#ffd60a", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontFamily: "monospace", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <Star size={10} strokeWidth={ICON_STROKE} /> AUTOMÁTICO
              </span>
            )}
            {alerta.disparado && (
              <span style={{ background: "#00e5a022", color: "#00e5a0", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontFamily: "monospace", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <CheckCircle2 size={10} strokeWidth={ICON_STROKE} /> DISPARADO
              </span>
            )}
          </div>
          <div style={{ color: cores.textSecondary, fontSize: "12px" }}>
            {alerta.tipo === "preco_exato"
              ? `Alerta quando atingir ${fmt(alerta.valor)}`
              : `Alerta quando ${alerta.direcao === "sobe" ? "subir" : "cair"} ${alerta.valor}%`}
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          <button onClick={() => onToggle(alerta.id, alerta.ativoFlag)}
            style={{ background: alerta.ativoFlag ? "#00e5a022" : cores.cardInner, border: `1px solid ${alerta.ativoFlag ? "#00e5a044" : cores.border}`, color: alerta.ativoFlag ? "#00e5a0" : cores.textFaint, borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer" }}>
            {alerta.ativoFlag ? "ON" : "OFF"}
          </button>
          <button onClick={() => onDelete(alerta.id)}
            style={{ background: "#ff4d6d22", border: "1px solid #ff4d6d44", color: "#ff4d6d", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center" }}>
            <Trash2 size={13} strokeWidth={ICON_STROKE} />
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
        <div style={{ background: cores.cardInner, borderRadius: "8px", padding: "8px 10px" }}>
          <div style={{ color: cores.textFaint, fontSize: "9px", fontFamily: "monospace" }}>PREÇO ATUAL</div>
          <div style={{ color: cores.textPrimary, fontSize: "14px", fontWeight: "700", fontFamily: "monospace" }}>{preco ? fmt(preco) : "..."}</div>
        </div>
        <div style={{ background: cores.cardInner, borderRadius: "8px", padding: "8px 10px" }}>
          <div style={{ color: cores.textFaint, fontSize: "9px", fontFamily: "monospace" }}>ALVO</div>
          <div style={{ color: cor, fontSize: "14px", fontWeight: "700", fontFamily: "monospace" }}>
            {alerta.tipo === "preco_exato" ? fmt(alerta.valor) : `${alerta.direcao === "sobe" ? "+" : "-"}${alerta.valor}%`}
          </div>
        </div>
        <div style={{ background: cores.cardInner, borderRadius: "8px", padding: "8px 10px" }}>
          <div style={{ color: cores.textFaint, fontSize: "9px", fontFamily: "monospace" }}>DISTÂNCIA</div>
          <div style={{ color: diff >= 0 ? "#00e5a0" : "#ff4d6d", fontSize: "14px", fontWeight: "700", fontFamily: "monospace" }}>
            {alerta.tipo === "preco_exato" && diff !== null ? `${diff >= 0 ? "+" : ""}${diff}%` : "—"}
          </div>
        </div>
      </div>

      {alerta.tipo === "preco_exato" && progressoPct && (
        <div style={{ marginTop: "10px" }}>
          <div style={{ height: "4px", background: cores.border, borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(progressoPct, 100)}%`, background: cor, borderRadius: "2px", transition: "width 0.5s" }} />
          </div>
        </div>
      )}

      <div style={{ color: cores.textFaint, fontSize: "10px", fontFamily: "monospace", marginTop: "8px" }}>
        Criado: {alerta.criadoEm}{alerta.disparadoEm && ` · Disparado: ${alerta.disparadoEm}`}
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────
export default function Alertas({ tema = "escuro" }) {
  const cores = paleta(tema);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [precos, setPrecos] = useState({});
  const [notificacao, setNotificacao] = useState(null);
  const [monitorando, setMonitorando] = useState(false);

  const [novoAtivo, setNovoAtivo] = useState("PETR4");
  const [novoTipo, setNovoTipo] = useState("preco_exato");
  const [novoDirecao, setNovoDirecao] = useState("sobe");
  const [novoValor, setNovoValor] = useState("");
  const [novoEmail, setNovoEmail] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aba, setAba] = useState("ativos");

  const alertasRef = useRef(alertas);
  alertasRef.current = alertas;

  // Carrega alertas do banco ao montar
  useEffect(() => {
    carregarAlertasBanco().then(data => {
      setAlertas(data);
      setLoading(false);
    });
  }, []);

  const fetchPrecos = useCallback(async () => {
    const ativos = [...new Set(alertasRef.current.map(a => a.ativo))];
    if (!ativos.length) return;
    try {
      const res = await fetch(`${PROXY}/api/prices?tickers=${ativos.join(",")}`);
      const data = await res.json();
      setPrecos(prev => ({ ...prev, ...data }));
      return data;
    } catch (e) { console.error(e); }
  }, []);

  const verificarAlertas = useCallback(async () => {
    const data = await fetchPrecos();
    if (!data) return;
    const agora = new Date().toLocaleTimeString("pt-BR");

    for (const alerta of alertasRef.current) {
      if (!alerta.ativoFlag || alerta.disparado) continue;
      const precoAtual = data[alerta.ativo]?.price;
      if (!precoAtual) continue;

      let disparar = false, tipo = "", mensagem = "";

      if (alerta.tipo === "preco_exato") {
        const diff = Math.abs((precoAtual - alerta.valor) / alerta.valor * 100);
        if (diff <= 0.5) {
          disparar = true;
          tipo = precoAtual >= alerta.valor ? "subiu" : "caiu";
          mensagem = `Atingiu o preço alvo de ${fmt(alerta.valor)}`;
        }
      } else if (alerta.tipo === "variacao_pct") {
        const variacao = data[alerta.ativo]?.change || 0;
        if (alerta.direcao === "sobe" && variacao >= alerta.valor) {
          disparar = true; tipo = "subiu";
          mensagem = `Subiu ${variacao.toFixed(2)}% (alvo: +${alerta.valor}%)`;
        } else if (alerta.direcao === "cai" && variacao <= -alerta.valor) {
          disparar = true; tipo = "caiu";
          mensagem = `Caiu ${Math.abs(variacao).toFixed(2)}% (alvo: -${alerta.valor}%)`;
        }
      }

      if (disparar) {
        // Atualiza no banco
        await atualizarAlertaBanco(alerta.id, { disparado: true, precoDisparo: precoAtual });

        // Atualiza estado local
        setAlertas(prev => prev.map(a =>
          a.id === alerta.id
            ? { ...a, disparado: true, precoDisparo: precoAtual, disparadoEm: agora }
            : a
        ));

        setNotificacao({ ativo: alerta.ativo, tipo, mensagem, precoAtual });

        if (alerta.email) {
          sendEmail({
            tipo_sinal: `ALERTA: ${alerta.ativo}`,
            ativo: alerta.ativo, preco: fmt(precoAtual),
            stop_loss: "—", take_profit: fmt(alerta.valor),
            confianca: "—", analise: mensagem,
          });
        }
      }
    }
  }, [fetchPrecos]);

  useEffect(() => {
    if (!monitorando) return;
    verificarAlertas();
    const interval = setInterval(verificarAlertas, 30000);
    return () => clearInterval(interval);
  }, [monitorando, verificarAlertas]);

  useEffect(() => {
    const temAtivos = alertas.some(a => a.ativoFlag && !a.disparado);
    setMonitorando(temAtivos);
  }, [alertas]);

  const criarAlerta = async () => {
    if (!novoValor || isNaN(parseFloat(novoValor))) return;
    setSalvando(true);

    const novoAlerta = {
      ativo: novoAtivo, tipo: novoTipo, direcao: novoDirecao,
      valor: parseFloat(novoValor), email: novoEmail,
    };

    const id = await criarAlertaBanco(novoAlerta);
    if (id) {
      setAlertas(prev => [{
        ...novoAlerta, id, ativoFlag: true, disparado: false,
        criadoEm: new Date().toLocaleString("pt-BR"), disparadoEm: null,
      }, ...prev]);
    }

    setNovoValor("");
    setSalvando(false);
    setAba("ativos");
  };

  const deletarAlerta = async (id) => {
    await deletarAlertaBanco(id);
    setAlertas(prev => prev.filter(a => a.id !== id));
  };

  const toggleAlerta = async (id, ativoAtual) => {
    const novoFlag = !ativoAtual;
    await atualizarAlertaBanco(id, { ativoFlag: novoFlag, disparado: false });
    setAlertas(prev => prev.map(a =>
      a.id === id ? { ...a, ativoFlag: novoFlag, disparado: false } : a
    ));
  };

  const alertasAtivos = alertas.filter(a => a.ativoFlag && !a.disparado);
  const alertasDisparados = alertas.filter(a => a.disparado);

  return (
    <div style={{ padding: "14px", maxWidth: "800px", margin: "0 auto" }}>
      {notificacao && <AlertaNotificacao alerta={notificacao} onDismiss={() => setNotificacao(null)} cores={cores} />}

      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: "700", marginBottom: "4px", color: cores.textPrimary, display: "flex", alignItems: "center", gap: "8px" }}>
            <Bell size={20} strokeWidth={ICON_STROKE} color="#8b5cf6" /> Alertas de Preço
          </h1>
          <p style={{ color: cores.textFaint, fontSize: "12px" }}>Monitoramento automático a cada 30 segundos</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: monitorando ? "#00e5a0" : cores.textFaint, animation: monitorando ? "pulse 2s infinite" : "none" }} />
          <span style={{ color: monitorando ? "#00e5a0" : cores.textFaint, fontSize: "11px", fontFamily: "monospace" }}>
            {monitorando ? `MONITORANDO (${alertasAtivos.length})` : "PARADO"}
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "10px", marginBottom: "16px" }}>
        {[
          { label: "ATIVOS", value: alertasAtivos.length, color: "#00e5a0" },
          { label: "DISPARADOS", value: alertasDisparados.length, color: "#ffd60a" },
          { label: "TOTAL", value: alertas.length, color: cores.textPrimary },
        ].map((s, i) => (
          <div key={i} style={{ background: cores.card, border: `1px solid ${cores.border}`, borderRadius: "10px", padding: "12px 14px", textAlign: "center" }}>
            <div style={{ color: cores.textFaint, fontSize: "9px", fontFamily: "monospace", marginBottom: "4px" }}>{s.label}</div>
            <div style={{ color: s.color, fontSize: "24px", fontWeight: "700" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "4px", background: cores.card, border: `1px solid ${cores.border}`, borderRadius: "10px", padding: "4px", marginBottom: "14px" }}>
        {[
          { id: "ativos", label: `Ativos (${alertasAtivos.length})`, Icone: Bell },
          { id: "criar", label: "Criar Alerta", Icone: Plus },
          { id: "historico", label: `Histórico (${alertasDisparados.length})`, Icone: History },
        ].map(tab => (
          <button key={tab.id} className="tab-btn" onClick={() => setAba(tab.id)}
            style={{ flex: 1, background: aba === tab.id ? "#8b5cf615" : "transparent", border: aba === tab.id ? "1px solid #8b5cf633" : "1px solid transparent", color: aba === tab.id ? "#8b5cf6" : cores.textFaint, borderRadius: "7px", padding: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <tab.Icone size={13} strokeWidth={ICON_STROKE} /> {tab.label}
          </button>
        ))}
      </div>

      {aba === "ativos" && (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#00e5a0", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <Loader2 size={22} strokeWidth={ICON_STROKE} className="spin" /> Carregando alertas...
            </div>
          ) : alertasAtivos.length === 0 ? (
            <div style={{ background: cores.card, border: `1px solid ${cores.border}`, borderRadius: "12px", padding: "40px", textAlign: "center" }}>
              <BellOff size={36} strokeWidth={ICON_STROKE} color={cores.textFaint} style={{ marginBottom: "12px" }} />
              <div style={{ color: cores.textFaint, fontSize: "14px", marginBottom: "8px" }}>Nenhum alerta ativo</div>
              <button onClick={() => setAba("criar")}
                style={{ background: "linear-gradient(135deg,#8b5cf6,#006eff)", color: "#000", border: "none", borderRadius: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: "700", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Plus size={14} strokeWidth={ICON_STROKE} /> Criar primeiro alerta
              </button>
            </div>
          ) : (
            alertasAtivos.map(a => (
              <AlertaCard key={a.id} alerta={a} precoAtual={precos[a.ativo]?.price} onDelete={deletarAlerta} onToggle={toggleAlerta} cores={cores} />
            ))
          )}
        </div>
      )}

      {aba === "criar" && (
        <div style={{ background: cores.card, border: `1px solid ${cores.border}`, borderRadius: "12px", padding: "20px" }}>
          <div style={{ color: cores.textFaint, fontSize: "10px", fontFamily: "monospace", letterSpacing: "0.1em", marginBottom: "16px" }}>NOVO ALERTA</div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", color: cores.textSecondary, fontSize: "11px", marginBottom: "5px" }}>Ativo</label>
            <select value={novoAtivo} onChange={e => setNovoAtivo(e.target.value)}
              style={{ width: "100%", background: cores.cardInner, border: `1px solid ${cores.border}`, color: cores.textPrimary, borderRadius: "8px", padding: "10px 12px", fontSize: "14px", fontFamily: "monospace" }}>
              {TODOS_ATIVOS.map(a => <option key={a} value={a}>{a}{precos[a] ? ` · R$${precos[a].price?.toFixed(2)}` : ""}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", color: cores.textSecondary, fontSize: "11px", marginBottom: "5px" }}>Tipo de alerta</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {[{ v: "preco_exato", l: "Preço exato", Icone: DollarSign }, { v: "variacao_pct", l: "Variação %", Icone: Percent }].map(t => (
                <button key={t.v} onClick={() => setNovoTipo(t.v)}
                  style={{ background: novoTipo === t.v ? "#8b5cf622" : cores.cardInner, border: `1px solid ${novoTipo === t.v ? "#8b5cf6" : cores.border}`, color: novoTipo === t.v ? "#8b5cf6" : cores.textSecondary, borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  <t.Icone size={14} strokeWidth={ICON_STROKE} /> {t.l}
                </button>
              ))}
            </div>
          </div>

          {novoTipo === "variacao_pct" && (
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", color: cores.textSecondary, fontSize: "11px", marginBottom: "5px" }}>Direção</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {[{ v: "sobe", l: "Sobe X%", Icone: TrendingUp }, { v: "cai", l: "Cai X%", Icone: TrendingDown }].map(d => (
                  <button key={d.v} onClick={() => setNovoDirecao(d.v)}
                    style={{ background: novoDirecao === d.v ? (d.v === "sobe" ? "#00e5a022" : "#ff4d6d22") : cores.cardInner, border: `1px solid ${novoDirecao === d.v ? (d.v === "sobe" ? "#00e5a0" : "#ff4d6d") : cores.border}`, color: novoDirecao === d.v ? (d.v === "sobe" ? "#00e5a0" : "#ff4d6d") : cores.textSecondary, borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                    <d.Icone size={14} strokeWidth={ICON_STROKE} /> {d.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", color: cores.textSecondary, fontSize: "11px", marginBottom: "5px" }}>
              {novoTipo === "preco_exato" ? "Preço alvo (R$)" : "Variação alvo (%)"}
            </label>
            <input type="number" value={novoValor} onChange={e => setNovoValor(e.target.value)} step="0.01"
              placeholder={novoTipo === "preco_exato" ? "Ex: 45.50" : "Ex: 5"}
              style={{ width: "100%", background: cores.cardInner, border: `1px solid ${cores.border}`, color: cores.textPrimary, borderRadius: "8px", padding: "12px 14px", fontSize: "16px", fontFamily: "monospace" }} />
            {novoTipo === "preco_exato" && precos[novoAtivo]?.price && (
              <div style={{ color: cores.textFaint, fontSize: "11px", marginTop: "4px", fontFamily: "monospace" }}>
                Preço atual: R${precos[novoAtivo].price.toFixed(2)}
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: cores.cardInner, borderRadius: "8px", padding: "10px 12px", marginBottom: "16px" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <Mail size={16} strokeWidth={ICON_STROKE} color={cores.textSecondary} style={{ flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ color: cores.textSecondary, fontSize: "12px" }}>Notificação por email</div>
                <div style={{ color: cores.textFaint, fontSize: "10px" }}>Receber email quando disparar</div>
              </div>
            </div>
            <button onClick={() => setNovoEmail(e => !e)}
              style={{ background: novoEmail ? "#00e5a022" : cores.cardInner, border: `1px solid ${novoEmail ? "#00e5a0" : cores.border}`, color: novoEmail ? "#00e5a0" : cores.textFaint, borderRadius: "6px", padding: "6px 14px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
              {novoEmail ? "ON ✓" : "OFF"}
            </button>
          </div>

          <button onClick={criarAlerta} disabled={!novoValor || salvando}
            style={{ width: "100%", background: !novoValor ? cores.border : "linear-gradient(135deg,#8b5cf6,#006eff)", color: !novoValor ? cores.textFaint : "#000", border: "none", borderRadius: "10px", padding: "14px", fontSize: "15px", fontWeight: "700", cursor: !novoValor ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            {salvando
              ? <><Loader2 size={16} strokeWidth={ICON_STROKE} className="spin" /> Salvando...</>
              : <><Bell size={16} strokeWidth={ICON_STROKE} /> Criar Alerta</>}
          </button>
        </div>
      )}

      {aba === "historico" && (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#00e5a0", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <Loader2 size={22} strokeWidth={ICON_STROKE} className="spin" /> Carregando...
            </div>
          ) : alertasDisparados.length === 0 ? (
            <div style={{ background: cores.card, border: `1px solid ${cores.border}`, borderRadius: "12px", padding: "40px", textAlign: "center" }}>
              <History size={36} strokeWidth={ICON_STROKE} color={cores.textFaint} style={{ marginBottom: "12px" }} />
              <div style={{ color: cores.textFaint, fontSize: "14px" }}>Nenhum alerta disparado ainda</div>
            </div>
          ) : (
            alertasDisparados.map(a => (
              <AlertaCard key={a.id} alerta={a} precoAtual={precos[a.ativo]?.price} onDelete={deletarAlerta} onToggle={toggleAlerta} cores={cores} />
            ))
          )}
        </div>
      )}

      <div style={{ padding: "10px 14px", background: cores.card, border: `1px solid ${cores.border}`, borderRadius: "10px", marginTop: "14px", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
        <span style={{ color: cores.textFaint, fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px" }}><Database size={12} strokeWidth={ICON_STROKE} /> Alertas salvos no banco Supabase</span>
        <span style={{ color: cores.textFaint, fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px" }}><RefreshCw size={12} strokeWidth={ICON_STROKE} /> Verificação a cada 30s</span>
        <span style={{ color: cores.textFaint, fontSize: "11px", display: "inline-flex", alignItems: "center", gap: "5px" }}><Mail size={12} strokeWidth={ICON_STROKE} /> Email via EmailJS</span>
      </div>
    </div>
  );
}
